'use client';

import clsx from 'clsx';
import React, { useEffect,useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Modal from '@/components/modal/modal';
import {
  ExampleTypeEnum,
  Source,
  SOURCE_TYPES,
  SourceConfig,
  SourceCreateReq,
  SourceTypeEnum
} from '@/types/evals';

import styles from './modalSourceCreate.module.scss';
import { StepGeneral } from './StepGeneral';
import { StepSourceType } from './StepSourceType';

type ModalSourceCreateProps = {
  cbAction: ( data: SourceCreateReq ) => void;
  onCloseAction: () => void;
  sourceToEdit?: Source;
  isPending?: boolean;
};

type GeneralStepData = {
  name: string;
  description?: string;
  source_type: SourceTypeEnum;
};

type SourceTypeStepData = {
  example_type: ExampleTypeEnum;
  config: SourceConfig;
};

export const ModalSourceCreate: React.FC<ModalSourceCreateProps> = ( {
  cbAction,
  onCloseAction,
  sourceToEdit,
  isPending
} ) => {
  const [ currentStep, setCurrentStep ] = useState<'general' | 'source_type'>(
    sourceToEdit ? 'source_type' : 'general'
  );

  // Store data from each step
  const [ generalData, setGeneralData ] = useState<GeneralStepData | undefined>(
    sourceToEdit
      ? {
          name: sourceToEdit.name,
          description: sourceToEdit.description,
          source_type: sourceToEdit.source_type
        }
      : undefined
  );

  const [ sourceTypeData, setSourceTypeData ] = useState<SourceTypeStepData | undefined>(
    sourceToEdit
      ? {
          example_type: sourceToEdit.example_type,
          config: sourceToEdit.config
        }
      : undefined
  );

  // Handle ESC key to close modal
  useEffect( () => {
    const handleKeyDown = ( event: KeyboardEvent ) => {
      if ( event.key === 'Escape' && !isPending ) {
        onCloseAction();
      }
    };

    window.addEventListener( 'keydown', handleKeyDown );

    return () => {
      window.removeEventListener( 'keydown', handleKeyDown );
    };
  }, [ onCloseAction, isPending ] );

  const handleStepNext = ( stepData: GeneralStepData | SourceTypeStepData ) => {
    if ( currentStep === 'general' ) {
      setGeneralData( stepData as GeneralStepData );
      setCurrentStep( 'source_type' );
    } else {
      setSourceTypeData( stepData as SourceTypeStepData );
      handleFormSubmit( stepData as SourceTypeStepData );
    }
  };

  const goToPreviousStep = () => {
    setCurrentStep( 'general' );
  };

  const handleFormSubmit = ( finalStepData: SourceTypeStepData ) => {
    if ( !generalData ) {
      toast.error( 'Missing general information' );
      return;
    }

    try {
      // JSON sources have a different API structure - examples at top level
      if ( generalData.source_type === SOURCE_TYPES.JSON ) {
        const jsonConfig = finalStepData.config as { examples: unknown[] };
        const sourceData = {
          name: generalData.name,
          description: generalData.description,
          source_type: generalData.source_type,
          examples: jsonConfig.examples
        } as unknown as SourceCreateReq;

        cbAction( sourceData );
      } else {
        const sourceData: SourceCreateReq = {
          name: generalData.name,
          description: generalData.description,
          source_type: generalData.source_type,
          config: finalStepData.config,
          example_type: finalStepData.example_type
        };

        cbAction( sourceData );
      }
    } catch( error ) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Unable to build configuration'
      );
    }
  };

  const renderStep = () => {
    if ( currentStep === 'general' ) {
      return (
        <StepGeneral
          initialData={ generalData }
          onNext={ handleStepNext }
          onCancel={ onCloseAction }
          isPending={ isPending }
        />
      );
    }

    if ( currentStep === 'source_type' && generalData ) {
      return (
        <StepSourceType
          sourceType={ generalData.source_type }
          sourceName={ generalData.name }
          sourceDescription={ generalData.description }
          initialData={ sourceTypeData }
          onNext={ handleStepNext }
          onCancel={ onCloseAction }
          onBack={ goToPreviousStep }
          isPending={ isPending }
          isEditMode={ !!sourceToEdit }
        />
      );
    }

    return null;
  };

  return (
    <Modal>
      <div className={ clsx( styles.modal_wrapper, currentStep === 'general' && styles.modal_wrapper_compact ) }>
        <div className={ styles.modal_content }>
          {renderStep()}
        </div>
      </div>
    </Modal>
  );
};

export default ModalSourceCreate;
